import { useCallback, useLayoutEffect, useState } from 'react';

function placeNear(rect, width, height) {
  const pad = 8;
  const gap = 4;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let left = rect.right - width;
  if (left < pad) left = pad;
  if (left + width > vw - pad) left = Math.max(pad, vw - pad - width);

  const belowTop = rect.bottom + gap;
  const aboveTop = rect.top - height - gap;
  const fitsBelow = belowTop + height <= vh - pad;
  const fitsAbove = aboveTop >= pad;

  let top = belowTop;
  if (!fitsBelow && fitsAbove) {
    top = aboveTop;
  } else if (!fitsBelow) {
    const spaceBelow = vh - pad - belowTop;
    const spaceAbove = rect.top - gap - pad;
    top = spaceBelow >= spaceAbove ? belowTop : aboveTop;
    if (top < pad) top = pad;
    const maxTop = vh - pad - height;
    if (top > maxTop) top = Math.max(pad, maxTop);
  }
  return { top, left };
}

/** Keeps the portaled reminder popover next to its anchor. */
export function usePickerPlacement(pickerOpen, step, anchorRef, pickerRef) {
  const [pickerPos, setPickerPos] = useState({ top: 0, left: 0 });

  const placePicker = useCallback(() => {
    const anchor = anchorRef.current;
    const pop = pickerRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const width = pop?.offsetWidth || 280;
    const height = pop?.offsetHeight || 200;
    setPickerPos(placeNear(rect, width, height));
  }, [anchorRef, pickerRef]);

  useLayoutEffect(() => {
    if (!pickerOpen) return undefined;
    placePicker();
    const pop = pickerRef.current;
    const observer = pop && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(placePicker)
      : null;
    if (pop && observer) observer.observe(pop);
    window.addEventListener('resize', placePicker);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', placePicker);
    };
  }, [pickerOpen, step, placePicker, pickerRef]);

  return { pickerPos, placePicker };
}
